/**
 * Filtro point-in-zone per la mappa clienti.
 *
 * Una "zona" e' una geometria disegnata dall'utente via Geoman:
 *  - circle: centro + radius (metri). Match con distanza geodetica.
 *  - polygon/rectangle: anello di vertici latlng. Match con Turf
 *    point-in-polygon.
 *
 * Persistenza: serializzata in JSON in localStorage cosi' la "zona
 * operativa" e' ricaricabile tra sessioni.
 */
import L from 'leaflet';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point, polygon as turfPolygon } from '@turf/helpers';

export type DrawnZone =
  | {
      type: 'circle';
      center: { lat: number; lng: number };
      radius: number; // meters
    }
  | {
      type: 'polygon' | 'rectangle';
      /** Anello di vertici (lat, lng). Il primo e l'ultimo devono coincidere. */
      latlngs: [number, number][];
    };

export const ZONE_STORAGE_KEY = 'dryconfig.customersMap.zone.v1';

/** Vero se il punto (lat, lng) e' dentro la zona. */
export function isPointInZone(lat: number, lng: number, zone: DrawnZone): boolean {
  if (zone.type === 'circle') {
    const a = L.latLng(lat, lng);
    const b = L.latLng(zone.center.lat, zone.center.lng);
    return a.distanceTo(b) <= zone.radius;
  }
  // GeoJSON usa [lng, lat] (x, y), NON [lat, lng]. Conversione obbligatoria.
  const ring = zone.latlngs.map(([la, ln]) => [ln, la]);
  // Garantisco chiusura dell'anello (primo = ultimo)
  if (ring.length > 0) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      ring.push([first[0], first[1]]);
    }
  }
  try {
    const poly = turfPolygon([ring]);
    return booleanPointInPolygon(point([lng, lat]), poly);
  } catch (e) {
    console.warn('[zoneFilter] invalid polygon, ignoring:', e);
    return false;
  }
}

export function loadZoneFromStorage(): DrawnZone | null {
  try {
    const raw = localStorage.getItem(ZONE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.type === 'circle' || parsed.type === 'polygon' || parsed.type === 'rectangle')) {
      return parsed as DrawnZone;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveZoneToStorage(zone: DrawnZone | null): void {
  try {
    if (zone) {
      localStorage.setItem(ZONE_STORAGE_KEY, JSON.stringify(zone));
    } else {
      localStorage.removeItem(ZONE_STORAGE_KEY);
    }
  } catch (e) {
    console.warn('[zoneFilter] localStorage save failed:', e);
  }
}
